// src/app/pages/app/TradeHistoryPage.jsx
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getMyTradeHistoryPaged } from "@app/features/trade/tradeApi";
import {
  History,
  TrendingUp,
  TrendingDown,
  Clock,
  CheckCircle,
  XCircle,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Wallet,
  Target,
  ArrowUp,
  ArrowDown,
} from "lucide-react";

function Badge({ children, tone = "gray" }) {
  const tones = {
    gray: "bg-gray-800/60 border-gray-700 text-gray-200",
    green: "bg-emerald-500/20 border-emerald-500/30 text-emerald-300",
    red: "bg-red-500/20 border-red-500/30 text-red-300",
    yellow: "bg-amber-500/20 border-amber-500/30 text-amber-300",
    blue: "bg-blue-500/20 border-blue-500/30 text-blue-300",
    purple: "bg-purple-500/20 border-purple-500/30 text-purple-300",
  };
  return (
    <span className={`px-2.5 py-1 rounded-lg border text-xs font-semibold ${tones[tone] || tones.gray}`}>
      {children}
    </span>
  );
}

function fmtNum(x, dp = 2) {
  const n = Number(x);
  if (!Number.isFinite(n)) return "-";
  return n.toLocaleString(undefined, { maximumFractionDigits: dp });
}

function fmtDate(x) {
  if (!x) return "-";
  try {
    const d = new Date(x);
    if (Number.isNaN(d.getTime())) return String(x);
    return d.toLocaleString();
  } catch {
    return String(x);
  }
}

function outcomeTone(outcome) {
  const o = String(outcome || "").toUpperCase();
  if (o === "WIN") return "green";
  if (o === "LOSE") return "red";
  if (o === "PUSH") return "yellow";
  return "gray";
}

function directionTone(dir) {
  const d = String(dir || "").toUpperCase();
  if (d === "UP" || d === "BUY" || d === "LONG") return "green";
  if (d === "DOWN" || d === "SELL" || d === "SHORT") return "red";
  return "gray";
}

function getDirectionIcon(dir) {
  const d = String(dir || "").toUpperCase();
  if (d === "UP" || d === "BUY" || d === "LONG") return <ArrowUp size={14} className="text-emerald-400" />;
  if (d === "DOWN" || d === "SELL" || d === "SHORT") return <ArrowDown size={14} className="text-red-400" />;
  return null;
}

function getOutcomeIcon(outcome) {
  const o = String(outcome || "").toUpperCase();
  if (o === "WIN") return <CheckCircle size={14} className="text-emerald-400" />;
  if (o === "LOSE") return <XCircle size={14} className="text-red-400" />;
  if (o === "PUSH") return <Clock size={14} className="text-amber-400" />;
  return null;
}

export default function TradeHistoryPage() {
  const [settledOnly, setSettledOnly] = useState(true);
  const [marketQ, setMarketQ] = useState("");
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);

  const q = useQuery({
    queryKey: ["myTradeHistory", { settledOnly, limit, offset }],
    queryFn: () => getMyTradeHistoryPaged({ settled_only: settledOnly, limit, offset }),
    keepPreviousData: true,
  });

  const items = q.data?.items || [];

  const filtered = useMemo(() => {
    const mq = (marketQ || "").trim().toUpperCase();
    if (!mq) return items;
    return items.filter((t) => String(t?.market || "").toUpperCase().includes(mq));
  }, [items, marketQ]);

  const hasMore = q.data?.hasMore === true;

  // Calculate summary stats
  const stats = useMemo(() => {
    const total = filtered.length;
    const wins = filtered.filter(t => String(t?.outcome || "").toUpperCase() === "WIN").length;
    const losses = filtered.filter(t => String(t?.outcome || "").toUpperCase() === "LOSE").length;
    const pushes = filtered.filter(t => String(t?.outcome || "").toUpperCase() === "PUSH").length;
    const winRate = total > 0 ? (wins / (wins + losses)) * 100 : 0;
    const totalPayout = filtered.reduce((sum, t) => sum + Number(t?.payout_amount || 0), 0);
    const totalStake = filtered.reduce((sum, t) => sum + Number(t?.stake_amount || 0), 0);
    const netPnL = totalPayout - totalStake;

    return { total, wins, losses, pushes, winRate, totalPayout, totalStake, netPnL };
  }, [filtered]);

  return (
    <div className="min-h-screen p-6 bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-indigo-500 via-purple-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-indigo-500/25">
            <History size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-2">
              Trade History
              <span className="text-xs font-medium bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
                Archive
              </span>
            </h1>
            <p className="text-sm text-gray-400">Complete record of all your trades and settlements</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20">
            <div className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-xs text-emerald-400 font-medium">{filtered.length} Trades</span>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
        {[
          { label: "Total Trades", value: stats.total, icon: History, color: "from-blue-500/20 to-cyan-500/20", textColor: "text-blue-400" },
          { label: "Wins", value: stats.wins, icon: CheckCircle, color: "from-emerald-500/20 to-green-500/20", textColor: "text-emerald-400" },
          { label: "Losses", value: stats.losses, icon: XCircle, color: "from-red-500/20 to-rose-500/20", textColor: "text-red-400" },
          { label: "Win Rate", value: stats.winRate.toFixed(1) + "%", icon: Target, color: "from-purple-500/20 to-indigo-500/20", textColor: "text-purple-400" },
          { label: "Net P&L", value: `$${fmtNum(stats.netPnL)}`, icon: Wallet, color: stats.netPnL >= 0 ? "from-emerald-500/20 to-green-500/20" : "from-red-500/20 to-rose-500/20", textColor: stats.netPnL >= 0 ? "text-emerald-400" : "text-red-400" },
          { label: "Total Payout", value: `$${fmtNum(stats.totalPayout)}`, icon: TrendingUp, color: "from-yellow-500/20 to-amber-500/20", textColor: "text-yellow-400" },
        ].map((stat) => (
          <div key={stat.label} className="rounded-xl bg-gradient-to-br from-gray-900 to-gray-950 border border-gray-800 p-3 text-center">
            <div className={`h-8 w-8 rounded-lg bg-gradient-to-br ${stat.color} flex items-center justify-center mx-auto mb-1`}>
              <stat.icon size={14} className={stat.textColor} />
            </div>
            <div className="text-xs text-gray-400">{stat.label}</div>
            <div className={`text-sm font-bold ${stat.textColor}`}>{stat.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            value={marketQ}
            onChange={(e) => setMarketQ(e.target.value)}
            placeholder="Search by market (e.g., BTCUSDT)..."
            className="w-full rounded-xl bg-gray-900/60 border border-gray-800 pl-10 pr-4 py-2.5 text-sm text-gray-200 outline-none focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/20 transition-all"
          />
        </div>

        <div className="flex gap-2">
          <label className="flex items-center gap-2 text-sm text-gray-300 px-4 py-2.5 rounded-xl bg-gray-900/60 border border-gray-800 hover:border-gray-700 transition-colors cursor-pointer">
            <input
              type="checkbox"
              checked={settledOnly}
              onChange={(e) => {
                setOffset(0);
                setSettledOnly(e.target.checked);
              }}
              className="accent-indigo-500 w-4 h-4"
            />
            <span className="flex items-center gap-1">
              <Filter size={14} />
              Settled only
            </span>
          </label>

          <select
            value={limit}
            onChange={(e) => {
              setOffset(0);
              setLimit(Number(e.target.value));
            }}
            className="rounded-xl bg-gray-900/60 border border-gray-800 px-4 py-2.5 text-sm text-gray-200 outline-none focus:border-indigo-500/50 transition-all"
          >
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-gray-900 to-gray-950 border border-gray-800 shadow-2xl shadow-black/20">
        <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/5 rounded-full blur-3xl" />

        <div className="relative">
          <div className="px-4 py-3 border-b border-gray-800/80 bg-gray-900/40 flex items-center justify-between">
            <div className="text-sm text-gray-300 flex items-center gap-2">
              <Sparkles size={14} className="text-indigo-400" />
              {q.isLoading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
                  Loading...
                </span>
              ) : (
                <span>{filtered.length} trade(s) found</span>
              )}
            </div>
            {q.isError ? (
              <div className="text-sm text-red-300 flex items-center gap-1">
                <XCircle size={14} />
                {String(q.error?.message || "Failed to load")}
              </div>
            ) : null}
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-900/50">
                <tr>
                  {["Market", "Direction", "Stake", "Entry", "Exit", "Outcome", "Payout", "Created", "Resolved"].map((header) => (
                    <th key={header} className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-800/50">
                {q.isLoading ? (
                  <tr>
                    <td className="px-4 py-12 text-center text-gray-400" colSpan={9}>
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-8 h-8 border-3 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
                        <span>Loading trade history…</span>
                      </div>
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td className="px-4 py-12 text-center text-gray-400" colSpan={9}>
                      <div className="flex flex-col items-center gap-2">
                        <div className="text-5xl mb-2">📭</div>
                        <p className="text-lg font-medium">No trades found</p>
                        <p className="text-sm text-gray-500">Start trading to build your history</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filtered.map((t) => {
                    const outcome = String(t?.outcome || "").toUpperCase();
                    const direction = String(t?.direction || "").toUpperCase();
                    const isWin = outcome === "WIN";
                    const isLoss = outcome === "LOSE";
                    
                    return (
                      <tr key={t.id} className="hover:bg-white/5 transition-colors group">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className={`h-8 w-8 rounded-lg bg-gradient-to-br ${
                              isWin ? 'from-emerald-500/20 to-green-500/20' :
                              isLoss ? 'from-red-500/20 to-rose-500/20' :
                              'from-gray-500/20 to-gray-600/20'
                            } flex items-center justify-center`}>
                              {getOutcomeIcon(outcome) || <History size={14} className="text-gray-400" />}
                            </div>
                            <span className="text-gray-100 font-medium">{t.market}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            {getDirectionIcon(direction)}
                            <Badge tone={directionTone(direction)}>{direction || "-"}</Badge>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-gray-200">
                            {fmtNum(t.stake_amount, 6)} <span className="text-gray-500 text-xs">{t.funded_asset}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-200">{fmtNum(t.entry_price, 2)}</td>
                        <td className="px-4 py-3 text-gray-200">{fmtNum(t.exit_price, 2)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            {getOutcomeIcon(outcome)}
                            <Badge tone={outcomeTone(outcome)}>{outcome || "-"}</Badge>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`font-semibold ${isWin ? 'text-emerald-400' : isLoss ? 'text-red-400' : 'text-gray-300'}`}>
                            ${fmtNum(t.payout_amount, 6)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-400 text-xs">{fmtDate(t.created_at)}</td>
                        <td className="px-4 py-3 text-gray-400 text-xs">{fmtDate(t.resolved_at)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-4 py-3 border-t border-gray-800/80 bg-gray-900/40 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="text-xs text-gray-500">
              Showing {offset + 1} - {Math.min(offset + limit, filtered.length)} of {filtered.length} trades
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={offset === 0 || q.isFetching}
                onClick={() => setOffset((x) => Math.max(0, x - limit))}
                className="flex items-center gap-1 px-4 py-2 rounded-xl border border-gray-800 bg-gray-900/50 text-gray-200 hover:border-gray-700 hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={16} />
                Previous
              </button>
              <span className="text-xs text-gray-500 px-3">
                Page {Math.floor(offset / limit) + 1}
              </span>
              <button
                type="button"
                disabled={!hasMore || q.isFetching}
                onClick={() => setOffset((x) => x + limit)}
                className="flex items-center gap-1 px-4 py-2 rounded-xl border border-gray-800 bg-gray-900/50 text-gray-200 hover:border-gray-700 hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Footer note */}
      <div className="mt-6 text-center text-xs text-gray-500">
        <span className="flex items-center justify-center gap-1">
          <Sparkles size={12} className="text-indigo-400" />
          All trades are permanently recorded on-chain for transparency and auditability
        </span>
      </div>
    </div>
  );
}
